/**
 * Anthra AI — Typed event registry
 *
 * MIRRORS config/analytics/event-design-sheet.yml, which is the source of truth
 * and is owned by Product (§15).
 *
 * WHY THIS FILE EXISTS
 *   An untyped `track('some_event', {...})` drifts from the sheet immediately and
 *   nobody notices until a dashboard is wrong — by which point the historical
 *   data is already unusable. Here, a typo in an event name, a missing required
 *   attribute or a value outside the allowed enum is a BUILD ERROR.
 *
 * ADDING AN EVENT
 *   1. Product adds it to config/analytics/event-design-sheet.yml, including the
 *      Expected Business Outcome. No entry, no implementation (§15).
 *   2. Add the schema here.
 *   3. Call `track('name', { ... })`. TypeScript enforces the rest.
 *
 * Never invent an event name at a call site. If the event you need is not here,
 * stop and ask Product (AGENTS.md §9).
 */

import { z } from "zod"

import { attachmentRoleSchema } from "../domain/attachment"
import { closeReasonSchema, feedbackRatingSchema } from "../domain/enums"
import { ApiError, NetworkError } from "../errors"
import { host } from "../host"
import { AnalyticsClient } from "./client"

// -----------------------------------------------------------------------------
// Shared attribute vocabularies
//
// Closed enumerations, deliberately. A free-text reason is unaggregatable, which
// makes the drop-off analysis §15 requires impossible.
// -----------------------------------------------------------------------------

/**
 * Which application a person is acting in.
 *
 * The user app and the Expert panel emit the same events against very different
 * populations, and a funnel that mixes forty experts into forty thousand users
 * answers nothing.
 */
export const authSurface = z.enum(["user_app", "expert_panel"])

/** Bounded failure buckets shared by query and vetting submission funnels. */
export const submissionFailureReasonSchema = z.enum([
	"validation_failed",
	"conflict",
	"network_error",
	"server_error",
	"request_rejected",
	"unknown",
])

export type SubmissionFailureReason = z.infer<typeof submissionFailureReasonSchema>

/** Maps an application error to a low-cardinality, PII-free analytics bucket. */
export function submissionFailureReason(error: unknown): SubmissionFailureReason {
	if (error instanceof NetworkError) return "network_error"
	if (!(error instanceof ApiError)) return "unknown"
	if (error.status === 409) return "conflict"
	if (error.status === 400 || error.status === 422) return "validation_failed"
	if (error.status >= 500) return "server_error"
	return "request_rejected"
}

// -----------------------------------------------------------------------------
// The registry
//
// Sixteen events, matching config/analytics/event-design-sheet.yml row for row.
// Names are object_action, snake_case, past tense.
//
// The kit ships this file with example events for commerce, courses, admissions
// and payments. They were removed rather than left: this product has no cart, no
// course, no application and no payment, and a registry carrying thirty schemas
// nothing emits reads as an implemented contract to the next person who opens it.
//
// Nothing below carries a name, a number, a notice, a description, a comment or
// an expert's words. That is the rule the WhatsApp templates work to
// (US-00-006.17), applied to the warehouse.
// -----------------------------------------------------------------------------

export const eventSchemas = {
	/**
	 * A code was asked for. The denominator for the whole product: everything
	 * else in the funnel is downstream of somebody typing their number.
	 */
	otp_requested: z.object({
		surface: authSurface,
		/** True when the entry screen revealed the profile fields. */
		is_new_number: z.boolean(),
		/** Separates a resend from a first request, for the cooldown's effect. */
		is_resend: z.boolean(),
	}),

	/** A valid OTP opened an authenticated session. */
	login_succeeded: z.object({
		surface: authSurface,
		is_new_user: z.boolean(),
	}),

	/** A query reached the pool. */
	query_submitted: z.object({
		attachment_count: z.number().int().min(0).max(3),
	}),

	/** A create-query request failed without carrying the request's content. */
	query_submission_failed: z.object({
		failure_reason: submissionFailureReasonSchema,
	}),

	/** A follow-up went into an existing thread. */
	query_follow_up_sent: z.object({
		attachment_count: z.number().int().min(0).max(3),
	}),

	/** The 'Was this helpful?' prompt was answered — and the query closed with it. */
	query_feedback_submitted: z.object({
		rating: feedbackRatingSchema,
		/** Whether a comment was written. NEVER the comment itself. */
		has_comment: z.boolean(),
	}),

	/** A notice and a draft went up for review. */
	vetting_request_submitted: z.object({}),

	/** A create-vetting request failed without carrying either document's content. */
	vetting_submission_failed: z.object({
		failure_reason: submissionFailureReasonSchema,
	}),

	/**
	 * The 'Was this helpful?' prompt was answered on a reviewed vetting request
	 * — and the request closed with it. Same shape as
	 * `query_feedback_submitted`, because the two prompts are the same prompt.
	 */
	vetting_feedback_submitted: z.object({
		rating: feedbackRatingSchema,
		/** Whether a comment was written. NEVER the comment itself. */
		has_comment: z.boolean(),
	}),

	/** A follow-up went into an existing vetting thread. No attachments here — see query_follow_up_sent. */
	vetting_follow_up_sent: z.object({}),

	/**
	 * A file was opened.
	 *
	 * Answers whether the preview paths are worth their complexity, and which of
	 * them people actually reach for (US-00-002.17, US-01-004.7).
	 */
	attachment_opened: z.object({
		attachment_role: attachmentRoleSchema,
		presentation: z.enum(["image", "pdf", "download_only"]),
		surface: authSurface,
	}),

	/**
	 * An Expert replied.
	 *
	 * `hours_since_submitted` on the FIRST response is the SLA figure the
	 * reporting dashboard averages, emitted here so the funnel and the dashboard
	 * can be reconciled against each other.
	 */
	expert_response_sent: z.object({
		is_first_response: z.boolean(),
		hours_since_submitted: z.number().nonnegative(),
	}),

	/** Mark as Answered. */
	query_marked_answered: z.object({
		hours_since_submitted: z.number().nonnegative(),
	}),

	/**
	 * A query reached Closed, by any of the three routes.
	 *
	 * The split between expert_close, user_feedback and inactivity is what says
	 * whether answered work is being confirmed or merely abandoned.
	 */
	query_closed: z.object({
		close_reason: closeReasonSchema,
	}),

	/** A vetting review was posted. */
	vetting_review_submitted: z.object({
		hours_since_submitted: z.number().nonnegative(),
	}),

	/**
	 * Someone acted on the Personalised Services banner (US-00-005).
	 *
	 * The only measure the banner has: it writes nothing, books nothing and
	 * charges nothing, so a tap on the number or the address is the entire
	 * signal that it is doing its job.
	 */
	personalised_services_contacted: z.object({
		channel: z.enum(["phone", "email"]),
		screen: z.string(),
	}),

	/** A shared render boundary displayed recovery UI to the user. */
	error_shown: z.object({
		error_code: z.literal("render_error"),
		is_retryable: z.boolean(),
	}),
} as const

export type EventName = keyof typeof eventSchemas
export type EventPayload<N extends EventName> = z.infer<(typeof eventSchemas)[N]>

// -----------------------------------------------------------------------------
// Client singleton
// -----------------------------------------------------------------------------

/** Accepts either prefix — Vite requires VITE_, Expo requires EXPO_PUBLIC_. */
function env(key: string, fallback = ""): string {
	const h = host()
	return h.env(`VITE_${key}`) ?? h.env(`EXPO_PUBLIC_${key}`) ?? fallback
}

/**
 * Built lazily, on first use, for the same reason as the logger: `installHost()`
 * runs in the app entry point, and a module-level constructor call would run at
 * import time — before it.
 */
let instance: AnalyticsClient | undefined

/**
 * THE ANALYTICS SWITCH. While false, analytics is off in every app: no provider
 * is registered or loaded, no event is tracked or sent, and no consent screen is
 * shown. It overrides the ANALYTICS_ENABLED setting, so a deployment variable
 * cannot turn it back on by accident. Set it to true to switch analytics back on;
 * ANALYTICS_ENABLED then applies again.
 */
export const ANALYTICS_ON = false

function build(): AnalyticsClient {
	const info = host().appInfo()
	return new AnalyticsClient({
		enabled: ANALYTICS_ON && env("ANALYTICS_ENABLED", "true") !== "false",
		debug: env("ANALYTICS_DEBUG_MODE") === "true",
		appVersion: info.version,
		// Reported by the host rather than hardcoded. Splitting a funnel by
		// platform is the first question asked of any metric once there is a mobile
		// app, and a constant 'web' makes it unanswerable.
		//
		// Narrowed rather than cast: appInfo().platform is a string (Platform.OS can
		// in principle be 'windows' or 'macos'), and an unexpected value must not
		// become a fourth platform in the warehouse that no dashboard groups by.
		platform: info.platform === "ios" || info.platform === "android" ? info.platform : "web",
		batchSize: Number(env("ANALYTICS_BATCH_SIZE", "20")),
		flushIntervalMs: Number(env("ANALYTICS_FLUSH_INTERVAL_MS", "10000")),
		requireConsent: env("ANALYTICS_REQUIRE_CONSENT") === "true",
	})
	// NOTE: no providers are registered here.
	//
	// They used to be, from './providers' — which loads Mixpanel, GA4 and Apxor by
	// injecting <script> tags. That import made the shared analytics singleton
	// unusable from an Expo app: script injection does not exist on native, and
	// the native SDKs are entirely different packages.
	//
	// Each app now registers its own, in its entry point:
	//
	//   web:    analytics.register(...createWebProviders())
	//   native: analytics.register(...createNativeProviders())
	//
	// One extra line per app, and the event contract, batching, redaction and
	// consent handling stay shared — which is the part that actually matters.
}

/**
 * The process-wide client.
 *
 * THE `set` TRAP IS NOT OPTIONAL — see the same note on `logger`. A method
 * reached through this Proxy runs with the Proxy as `this`, so `setConsent`,
 * `setScreen`, `setCorrelationId` and the `userId` bound by `identify` would all
 * write to the empty target and be read back as undefined. The visible symptoms
 * are an events stream where `screen_name` is forever "unknown" and no event
 * ever carries a `user_id` — and, where consent is required, a client that
 * emits nothing at all however many times consent is granted.
 */
export const analytics: AnalyticsClient = new Proxy({} as AnalyticsClient, {
	get(_target, property, receiver) {
		instance ??= build()
		return Reflect.get(instance, property, receiver) as unknown
	},
	set(_target, property, value, receiver) {
		instance ??= build()
		// Anything that inherited from this Proxy keeps its own state, exactly as
		// an ordinary [[Set]] would leave it.
		if (receiver !== analytics) {
			return Reflect.defineProperty(receiver as object, property, {
				value,
				writable: true,
				enumerable: true,
				configurable: true,
			})
		}
		return Reflect.set(instance, property, value)
	},
})

/**
 * The ONLY way application code emits an event.
 *
 * A misspelled name or a missing attribute fails the build, so the sheet and the
 * code cannot silently diverge.
 *
 *   track('query_submitted', {
 *     category: 'tds',
 *     attachment_count: files.length,
 *   });
 */
export function track<N extends EventName>(name: N, payload: EventPayload<N>): void {
	// Validate in development so a wrong value is caught while writing the code,
	// not in a QA verification pass three weeks later (ANALYTICS.md §10).
	// Validated outside production on BOTH platforms. `import.meta.env.DEV` is
	// Vite-only and does not exist under Metro.
	if (
		host().env("VITE_APP_ENV") !== "production" &&
		host().env("EXPO_PUBLIC_APP_ENV") !== "production"
	) {
		const result = eventSchemas[name].safeParse(payload)
		if (!result.success) {
			throw new Error(
				`analytics: event "${name}" does not match its schema:\n${JSON.stringify(
					result.error.flatten(),
					null,
					2,
				)}`,
			)
		}
	}
	analytics.track(name, payload)
}

export { analytics as analyticsClient }
export type { SessionAttributes, UserAttributes } from "./types"
